import axios from 'axios';

import { LogService } from '../../../../services/log-service';
import { createCancelablePromise } from '../../../../services/promises/create-cancelable-promise';
import type { TransferOrderStatus } from '../data/transfer-order-statuses';

const prefix = '/transferOrder';

export type TransferOrderGridRow = {
  transferOrderId: number;
  orderNumber: string;
  referenceNumber: string | null;
  originWarehouseId: number;
  destinationWarehouseId: number;
  status: string;
  createDate: string;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
};

export type TransferOrdersGridResponse = {
  transferOrders: TransferOrderGridRow[];
  totalCount: number;
};

export type TransferOrderGridRequest = {
  page?: {
    pageNumber: number;
    pageSize: number;
  };
};

export type TransferOrderUom = {
  productUnitOfMeasureId: number;
  productId: number;
  uomTypeId: number;
  uomTypeName: string;
  uomLevel: string;
  parentProductUnitOfMeasureId: number | null;
  conversionFactor: number;
  uomSku: string | null;
  barcode: string | null;
  isActive: boolean;
};

export type TransferOrderProduct = {
  id: number;
  productId: number;
  originLocationId: number;
  quantity: number;
  productUoMId?: number | null;
  quantityInUom?: number | null;
  lotUuid: string | null;
  lot: string | null;
  createDate: string | null;
  modifyDate: string | null;
  name: string | null;
  sku: string | null;
  thumbnailUrl: string | null;
  upc: string | null;
  totalReceived: number;
  totalRejected: number;
  unitsOfMeasure?: TransferOrderUom[] | null;
};

export type TransferOrderResponse = {
  transferOrderId: number;
  orderNumber: string;
  referenceNumber: string | null;
  notes: string | null;
  status: string;
  originWarehouseId: number;
  destinationWarehouseId: number;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  createDate: string | null;
  modifyDate: string | null;
  products: TransferOrderProduct[];
};

export type UpdateTransferOrderRequest = {
  originWarehouseId: number;
  destinationWarehouseId: number;
  status: TransferOrderStatus;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  referenceNumber: string | null;
  notes: string | null;
  products: Array<{
    productId: number;
    quantity: number;
    originLocationId: number;
    productUoMId?: number | null;
    quantityInUom?: number | null;
    lotUuid?: string | null;
  }>;
};

export const updateTransferOrder = (id: number, data: UpdateTransferOrderRequest): Promise<TransferOrderResponse> =>
  axios.put<TransferOrderResponse>(`${prefix}/${id}`, data).then((response) => response.data);

export const getTransferOrder = (id: number) => {
  const requestPromise = axios
    .get<TransferOrderResponse>(`${prefix}/${id}`)
    .then((response) => response?.data ?? null)
    .catch((error) => {
      LogService.logError(new Error('Failed to fetch transfer order from API', { cause: error }));
      return null;
    });

  return createCancelablePromise(requestPromise);
};

export const getTransferOrders = (pageNumber?: number, pageSize?: number) => {
  const payload: TransferOrderGridRequest = {};

  if (pageNumber !== undefined && pageSize !== undefined) {
    payload.page = {
      pageNumber,
      pageSize,
    };
  }

  const requestPromise = axios
    .post<TransferOrdersGridResponse>(`${prefix}/grid`, payload)
    .then((response) => {
      return response?.data ?? { transferOrders: [], totalCount: 0 };
    })
    .catch((error) => {
      LogService.logError(new Error('Failed to fetch transfer orders from API', { cause: error }));
      return { transferOrders: [], totalCount: 0 };
    });

  return createCancelablePromise(requestPromise);
};

export const patchTransferOrderStatus = async (
  transferOrderId: number,
  status: string,
  restoreInventory = false,
): Promise<TransferOrderResponse> => {
  const response = await axios.patch<TransferOrderResponse>(
    `${prefix}/${transferOrderId}/status`,
    { status },
    { params: { restoreInventory } },
  );
  return response.data;
};
